import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import * as api from 'api/api'
import { ApprovalListItem, ApprovalTab } from 'api/types'
import { useConfirm } from 'components/Confirm'
import { Icon } from 'components/Icon'
import { ReviewPanel } from 'components/ReviewPanel'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  IconButton,
  Modal,
  Spinner,
  TextAreaField,
  TextField,
} from 'components/ui'
import { WORKFLOW_LABELS, formatDate, formatDateTime } from 'lib/format'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'

const APPROVAL_TABS: ApprovalTab[] = ['PENDING', 'RETURNED', 'APPROVED']

const ReturnModal = ({
  count,
  busy,
  onConfirm,
  onClose,
}: {
  count: number
  busy: boolean
  onConfirm: (note: string) => void
  onClose: () => void
}) => {
  const [note, setNote] = useState('')
  return (
    <Modal
      title={
        count > 1 ? t(`إرسال ${count} تقارير للمراجعة`, `Send ${count} reports for review`) : t('إرسال التقرير للمراجعة', 'Send the report for review')
      }
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('إلغاء', 'Cancel')}</Button>
          <Button
            variant="danger"
            busy={busy}
            disabled={!note.trim()}
            onClick={() => onConfirm(note.trim())}
          >
            {t('إرسال للمراجعة', 'Send for review')}
          </Button>
        </>
      }
    >
      <TextAreaField
        label={t('السبب / الملاحظة', 'Reason / note')}
        hint={
          count > 1
            ? t('سيُطبَّق هذا السبب على جميع التقارير المحددة', 'This reason will apply to every selected report')
            : t('ستظهر الملاحظة لمهندس الموقع في أعلى التقرير', 'The site engineer will see this note at the top of the report')
        }
        rows={4}
        value={note}
        onChange={(event) => setNote(event.target.value)}
        required
      />
    </Modal>
  )
}

const paramTab = (value: string | null): ApprovalTab =>
  APPROVAL_TABS.includes(value as ApprovalTab) ? (value as ApprovalTab) : 'PENDING'

type Density = 'comfortable' | 'compact'
type ColumnKey = 'site' | 'engineer' | 'date' | 'statusDate'

export const ApprovalsPage = () => {
  const [params, setParams] = useSearchParams()
  const tab = paramTab(params.get('tab'))
  const [search, setSearch] = useState('')
  const [site, setSite] = useState('')
  const [engineer, setEngineer] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [returning, setReturning] = useState(false)
  const [density, setDensity] = useState<Density>('comfortable')
  const [columns, setColumns] = useState<Record<ColumnKey, boolean>>({
    date: true,
    engineer: true,
    site: true,
    statusDate: true,
  })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [reviewing, setReviewing] = useState<string | null>(null)
  const { busy, run } = useAction()
  const confirm = useConfirm()
  const columnLabels: Record<ColumnKey, string> = {
    date: t('التاريخ', 'Date'),
    engineer: t('المهندس', 'Engineer'),
    site: t('الموقع', 'Site'),
    statusDate: t('تاريخ الحالة', 'Status date'),
  }
  const tabs: { key: ApprovalTab; label: string }[] = [
    { key: 'PENDING', label: t('بانتظار الاعتماد', 'Waiting for approval') },
    { key: 'RETURNED', label: t('تقرير للمراجعة', 'Report for review') },
    { key: 'APPROVED', label: t('معتمدة', 'Approved') },
  ]

  const { data, error, loading, reload } = useAsync(
    () =>
      Promise.all([
        api.fetchApprovals('PENDING'),
        api.fetchApprovals('RETURNED'),
        api.fetchApprovals('APPROVED'),
      ]),
    []
  )
  const [pending, returned, approved] = data ?? [[], [], []]
  const byTab: Record<ApprovalTab, ApprovalListItem[]> = {
    APPROVED: approved,
    PENDING: pending,
    RETURNED: returned,
  }
  const items = byTab[tab]

  const sites = useMemo(
    () => Array.from(new Set(items.map((item) => item.projectName))).sort(),
    [items]
  )

  const engineers = useMemo(
    () =>
      Array.from(new Set(items.map((item) => item.preparedByName).filter((name): name is string => Boolean(name)))).sort(),
    [items]
  )

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase()
    return items.filter(
      (item) =>
        (!site || item.projectName === site) &&
        (!engineer || item.preparedByName === engineer) &&
        (!from || item.reportDate >= from) &&
        (!to || item.reportDate <= to) &&
        (!term ||
          item.code.toLowerCase().includes(term) ||
          item.projectName.toLowerCase().includes(term) ||
          (item.preparedByName ?? '').toLowerCase().includes(term))
    )
  }, [items, search, site, engineer, from, to])

  useEffect(() => {
    setPage(1)
  }, [tab, search, site, engineer, from, to, pageSize])

  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize))
  const safePage = Math.min(page, pageCount)
  const pageStart = visible.length ? (safePage - 1) * pageSize + 1 : 0
  const pageEnd = Math.min(safePage * pageSize, visible.length)
  const pageItems = visible.slice((safePage - 1) * pageSize, safePage * pageSize)

  const switchTab = (next: ApprovalTab) => {
    setSelected(new Set())
    const nextParams = new URLSearchParams(params)
    nextParams.set('tab', next)
    setParams(nextParams, { replace: true })
  }

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const pageAllSelected = pageItems.length > 0 && pageItems.every((item) => selected.has(item.id))

  const toggleAll = () =>
    setSelected((current) => {
      const next = new Set(current)
      if (pageAllSelected) pageItems.forEach((item) => next.delete(item.id))
      else pageItems.forEach((item) => next.add(item.id))
      return next
    })

  const approveOne = async (id: string) => {
    if (!(await confirm(t('اعتماد هذا التقرير؟', 'Approve this report?'), { confirmLabel: t('اعتماد', 'Approve'), tone: 'primary' }))) return
    const result = await run(
      `approve-${id}`,
      () => api.approveReports([id]),
      t('تم اعتماد التقرير', 'Report approved')
    )
    if (result) {
      setSelected((current) => {
        const next = new Set(current)
        next.delete(id)
        return next
      })
      reload()
    }
  }

  const approveSelected = async () => {
    if (!selected.size) return
    if (!(await confirm(t(`اعتماد ${selected.size} تقرير؟`, `Approve ${selected.size} reports?`), { confirmLabel: t('اعتماد', 'Approve'), tone: 'primary' }))) return
    const result = await run(
      'approve-bulk',
      () => api.approveReports(Array.from(selected)),
      t(`تم اعتماد ${selected.size} تقرير`, `${selected.size} reports approved`)
    )
    if (result) {
      setSelected(new Set())
      reload()
    }
  }

  const confirmReturn = async (note: string) => {
    const ids = Array.from(selected)
    const result = await run(
      'return-bulk',
      () => api.returnReports(ids, note),
      ids.length > 1
        ? t(`أصبحت ${ids.length} تقارير للمراجعة`, `${ids.length} reports are for review`)
        : t('أصبح التقرير للمراجعة', 'The report is for review')
    )
    if (result) {
      setSelected(new Set())
      setReturning(false)
      reload()
    }
  }

  const returnOne = (id: string) => {
    setSelected(new Set([id]))
    setReturning(true)
  }

  const exportReports = async () => {
    const ids = selected.size ? Array.from(selected) : visible.map((item) => item.id)
    if (!ids.length) return
    if (!(await confirm(t(`تصدير ${ids.length} تقرير كملفات PDF؟`, `Export ${ids.length} reports as PDF files?`), { confirmLabel: t('تصدير', 'Export'), tone: 'primary' }))) return
    await run(
      'export',
      async () => {
        for (const id of ids) {
          // eslint-disable-next-line no-await-in-loop
          await api.downloadReport(id, 'pdf')
        }
      },
      t(`تم تصدير ${ids.length} تقرير`, `${ids.length} reports exported`)
    )
  }

  return (
    <div className="page">
      <h1>{t('الاعتمادات', 'Approvals')}</h1>

      <div className="tabs-list" role="tablist" aria-label={t('حالة الاعتماد', 'Approval status')}>
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            className="tabs-trigger"
            aria-selected={tab === item.key}
            onClick={() => switchTab(item.key)}
          >
            {item.label}
            <span className="tabs-trigger__count">{byTab[item.key].length}</span>
          </button>
        ))}
      </div>

      <Card
        actions={
          <>
            <div className="search">
              <Icon name="search" size={16} />
              <label htmlFor="approvals-search" className="sr-only">
                {t('بحث', 'Search')}
              </label>
              <input
                id="approvals-search"
                type="search"
                placeholder={t('بحث برقم التقرير أو الموقع أو المهندس', 'Search by report number, site, or engineer')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            {sites.length > 1 ? (
              <div className="field">
                <select
                  className="input"
                  value={site}
                  onChange={(event) => setSite(event.target.value)}
                  aria-label={t('الموقع', 'Site')}
                >
                  <option value="">{t('كل المواقع', 'All sites')}</option>
                  {sites.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {engineers.length > 1 ? (
              <div className="field">
                <select
                  className="input"
                  value={engineer}
                  onChange={(event) => setEngineer(event.target.value)}
                  aria-label={t('المهندس', 'Engineer')}
                >
                  <option value="">{t('كل المهندسين', 'All engineers')}</option>
                  {engineers.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <TextField
              label={t('من تاريخ', 'From date')}
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
            <TextField
              label={t('إلى تاريخ', 'To date')}
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
            <details className="dropdown">
              <summary className="btn">{t('الأعمدة', 'Columns')}</summary>
              <div className="dropdown__panel">
                {(Object.keys(columnLabels) as ColumnKey[]).map((key) => (
                  <label key={key} className="dropdown__item">
                    <input
                      type="checkbox"
                      checked={columns[key]}
                      onChange={() => setColumns((current) => ({ ...current, [key]: !current[key] }))}
                    />
                    {columnLabels[key]}
                  </label>
                ))}
              </div>
            </details>
            <div className="segmented" role="group" aria-label={t('كثافة الجدول', 'Table density')}>
              <button type="button" aria-pressed={density === 'comfortable'} onClick={() => setDensity('comfortable')}>
                {t('مريح', 'Comfortable')}
              </button>
              <button type="button" aria-pressed={density === 'compact'} onClick={() => setDensity('compact')}>
                {t('مضغوط', 'Compact')}
              </button>
            </div>
            <Button icon="download" busy={busy === 'export'} disabled={!visible.length} onClick={exportReports}>
              {t('تصدير', 'Export')}{selected.size ? ` (${selected.size})` : ''}
            </Button>
          </>
        }
      >
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : !visible.length ? (
          tab === 'PENDING' ? (
            <EmptyState icon="check" title={t('الكل منجز', 'All caught up')}>
              <p className="muted">
                {t('لا توجد تقارير بانتظار اعتمادك. سنُعلمك عند إرسال مهندس الموقع لتقرير جديد.', 'No reports are waiting for your approval. We will let you know when a site engineer submits a new report.')}
              </p>
              <Button onClick={() => switchTab('APPROVED')}>
                {t('عرض التقارير المعتمدة', 'View approved reports')}
              </Button>
            </EmptyState>
          ) : (
            <EmptyState title={t('لا توجد تقارير مطابقة', 'No matching reports')} icon="list" />
          )
        ) : (
          <>
          <div className="table-wrap">
            <table className={density === 'compact' ? 'table table--compact' : 'table'}>
              <thead>
                <tr>
                  <th scope="col">
                    <input
                      type="checkbox"
                      aria-label={t('تحديد الكل', 'Select all')}
                      checked={pageAllSelected}
                      onChange={toggleAll}
                    />
                  </th>
                  <th scope="col">{t('التقرير', 'Report')}</th>
                  {columns.site ? <th scope="col">{columnLabels.site}</th> : null}
                  {columns.engineer ? <th scope="col">{columnLabels.engineer}</th> : null}
                  {columns.date ? <th scope="col">{columnLabels.date}</th> : null}
                  {columns.statusDate ? (
                    <th scope="col">
                      {tab === 'PENDING'
                        ? t('تاريخ الإرسال', 'Submitted date')
                        : tab === 'RETURNED'
                          ? t('تاريخ المراجعة', 'Review date')
                          : t('تاريخ الاعتماد', 'Approved date')}
                    </th>
                  ) : null}
                  <th scope="col">
                    <span className="sr-only">{t('إجراءات', 'Actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((item) => (
                  <tr key={item.id} className={selected.has(item.id) ? 'is-selected' : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={t(`تحديد ${item.code}`, `Select ${item.code}`)}
                        checked={selected.has(item.id)}
                        onChange={() => toggle(item.id)}
                      />
                    </td>
                    <td>
                      <Link
                        to={`/projects/${item.projectId}/reports/${item.id}`}
                        className="code-link"
                      >
                        {item.code}
                      </Link>
                      <span className="chip">
                        {WORKFLOW_LABELS[item.workflow]}
                      </span>
                    </td>
                    {columns.site ? <td>{item.projectName}</td> : null}
                    {columns.engineer ? <td>{item.preparedByName || '—'}</td> : null}
                    {columns.date ? <td className="num">{formatDate(item.reportDate)}</td> : null}
                    {columns.statusDate ? (
                      <td className="num">
                        {formatDateTime(
                          tab === 'PENDING'
                            ? item.submittedAt
                            : tab === 'RETURNED'
                              ? item.returnedAt
                              : item.approvedAt
                        ) || '—'}
                      </td>
                    ) : null}
                    <td>
                      <div className="row-actions">
                        {tab === 'PENDING' ? (
                          <>
                            <IconButton
                              icon="check"
                              label={t(`اعتماد — ${item.code}`, `Approve — ${item.code}`)}
                              disabled={busy === `approve-${item.id}`}
                              onClick={() => approveOne(item.id)}
                            />
                            <IconButton
                              icon="undo"
                              label={t(`إرسال للمراجعة — ${item.code}`, `Send for review — ${item.code}`)}
                              onClick={() => returnOne(item.id)}
                            />
                          </>
                        ) : null}
                        {tab === 'PENDING' ? (
                          <IconButton
                            icon="chevronLeft"
                            label={t(`فتح المراجعة — ${item.code}`, `Open review — ${item.code}`)}
                            onClick={() => setReviewing(item.id)}
                          />
                        ) : (
                          <Link
                            to={`/projects/${item.projectId}/reports/${item.id}`}
                            className="icon-button"
                            aria-label={t(`فتح المراجعة — ${item.code}`, `Open review — ${item.code}`)}
                            title={t(`فتح المراجعة — ${item.code}`, `Open review — ${item.code}`)}
                          >
                            <Icon name="chevronLeft" />
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="table-footer">
            <span className="muted small">
              {t(`${selected.size} من ${visible.length} محدد`, `${selected.size} of ${visible.length} selected`)}
            </span>
            <span className="spacer" />
            <label className="table-footer__rows">
              {t('صفوف لكل صفحة', 'Rows per page')}
              <select className="input" value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </label>
            <span className="muted small">
              {pageStart}–{pageEnd} {t('من', 'of')} {visible.length}
            </span>
            <div className="table-footer__pagination">
              <IconButton
                icon="chevronsRight"
                label={t('الصفحة الأولى', 'First page')}
                disabled={safePage <= 1}
                onClick={() => setPage(1)}
              />
              <IconButton
                icon="chevronRight"
                label={t('الصفحة السابقة', 'Previous page')}
                disabled={safePage <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              />
              <IconButton
                icon="chevronLeft"
                label={t('الصفحة التالية', 'Next page')}
                disabled={safePage >= pageCount}
                onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              />
              <IconButton
                icon="chevronsLeft"
                label={t('الصفحة الأخيرة', 'Last page')}
                disabled={safePage >= pageCount}
                onClick={() => setPage(pageCount)}
              />
            </div>
          </div>
          </>
        )}
      </Card>

      {selected.size ? (
        <div className="bulk-bar" role="toolbar" aria-label={t('إجراءات التحديد', 'Selection actions')}>
          <span className="bulk-bar__count">{t(`${selected.size} محدد`, `${selected.size} selected`)}</span>
          <span className="bulk-bar__separator" />
          {tab === 'PENDING' ? (
            <>
              <Button icon="check" busy={busy === 'approve-bulk'} onClick={approveSelected}>
                {t('اعتماد', 'Approve')}
              </Button>
              <Button icon="undo" onClick={() => setReturning(true)}>
                {t('إرسال للمراجعة', 'Send for review')}
              </Button>
            </>
          ) : null}
          <Button icon="download" busy={busy === 'export'} onClick={exportReports}>
            {t('تصدير', 'Export')}
          </Button>
          <span className="bulk-bar__separator" />
          <Button icon="close" onClick={() => setSelected(new Set())}>
            {t('إلغاء', 'Cancel')}
          </Button>
        </div>
      ) : null}

      {returning ? (
        <ReturnModal
          count={selected.size}
          busy={busy === 'return-bulk'}
          onConfirm={confirmReturn}
          onClose={() => setReturning(false)}
        />
      ) : null}

      {reviewing ? (
        <ReviewPanel
          reportId={reviewing}
          onClose={() => setReviewing(null)}
          onDecided={() => {
            setReviewing(null)
            reload()
          }}
        />
      ) : null}
    </div>
  )
}
